import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { api } from '../services/api';
import { publishIncident } from '../services/socket';
import { Answer, Candidate, ExamState, Incident, ProctorMetrics } from '../types/domain';
import { questions } from '../utils/examData';
import { createReportExpiry } from '../utils/candidateReport';
import { applyObservationCondition, finalizeObservations, ObservationCondition } from '../utils/observations';

const defaultMetrics: ProctorMetrics = {
  riskScore: 8,
  attentionScore: 96,
  eyeContactPercent: 94,
  faceVisibilityPercent: 98,
  noiseLevel: 6,
  cameraHealth: 'good',
  microphoneHealth: 'good',
  warnings: []
};

const emptyState: ExamState = {
  activeQuestionId: questions[0]?.id ?? '',
  answers: {},
  incidents: [],
  observations: [],
  metrics: defaultMetrics,
  workflow: { instructionsAcknowledged: false, permissionsCompleted: false, cameraSetupCompleted: false }
};

function restoreState(): ExamState {
  try {
    const saved = sessionStorage.getItem('examSphereSession');
    if (!saved) return emptyState;
    const parsed = JSON.parse(saved) as Partial<ExamState>;
    return { ...emptyState, ...parsed, observations: parsed.observations ?? [], workflow: { ...emptyState.workflow, ...parsed.workflow } };
  } catch { return emptyState; }
}

type Action =
  | { type: 'login'; candidate: Candidate }
  | { type: 'start' }
  | { type: 'finish'; finishedAt: string; reportExpiresAt: string }
  | { type: 'answer'; answer: Answer }
  | { type: 'active'; questionId: string }
  | { type: 'incident'; incident: Incident }
  | { type: 'observation'; condition: ObservationCondition }
  | { type: 'metrics'; metrics: Partial<ProctorMetrics> }
  | { type: 'calibration'; checks: string[]; cameraCategory?: 'front' | 'rear' | 'unknown' }
  | { type: 'workflow'; key: keyof ExamState['workflow'] }
  | { type: 'logout' };

function reducer(state: ExamState, action: Action): ExamState {
  switch (action.type) {
    case 'login':
      if (state.candidate?.sessionId === action.candidate.sessionId) return { ...state, candidate: action.candidate };
      return { ...emptyState, candidate: action.candidate };
    case 'start':
      return { ...state, startedAt: new Date().toISOString() };
    case 'finish':
      return {
        ...state,
        observations: finalizeObservations(state.observations, Date.parse(action.finishedAt)),
        finishedAt: action.finishedAt,
        reportExpiresAt: action.reportExpiresAt,
        reportSessionId: state.candidate?.sessionId
      };
    case 'answer':
      return { ...state, answers: { ...state.answers, [action.answer.questionId]: action.answer } };
    case 'active':
      return { ...state, activeQuestionId: action.questionId };
    case 'incident':
      return {
        ...state,
        incidents: [action.incident, ...state.incidents].slice(0, 500)
      };
    case 'observation': {
      const observations = applyObservationCondition(state.observations, action.condition);
      return observations === state.observations ? state : { ...state, observations };
    }
    case 'metrics':
      if (Object.entries(action.metrics).every(([key, value]) => Object.is(state.metrics[key as keyof ProctorMetrics], value))) {
        return state;
      }
      return { ...state, metrics: { ...state.metrics, ...action.metrics } };
    case 'calibration':
      return {
        ...state,
        calibration: {
          completedAt: new Date().toISOString(),
          mandatoryChecksPassed: action.checks,
          readiness: 'ready',
          selectedCameraCategory: action.cameraCategory
        },
        workflow: { ...state.workflow, cameraSetupCompleted: true }
      };
    case 'workflow':
      return { ...state, workflow: { ...state.workflow, [action.key]: true } };
    case 'logout':
      return emptyState;
    default:
      return state;
  }
}

interface ExamContextValue {
  state: ExamState;
  login: (candidate: Candidate) => void;
  startExam: () => void;
  finishExam: () => void;
  setAnswer: (answer: Answer) => void;
  setActiveQuestion: (questionId: string) => void;
  addIncident: (incident: Incident) => void;
  recordObservationCondition: (condition: ObservationCondition) => void;
  updateMetrics: (metrics: Partial<ProctorMetrics>) => void;
  completeWorkflowStep: (key: keyof ExamState['workflow']) => void;
  completeCalibration: (checks: string[], cameraCategory?: 'front' | 'rear' | 'unknown') => void;
  logout: () => void;
}

const ExamContext = createContext<ExamContextValue | undefined>(undefined);

export function ExamProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, restoreState);

  useEffect(() => {
    sessionStorage.setItem('examSphereSession', JSON.stringify(state));
  }, [state]);

  const addIncident = useCallback((incident: Incident) => {
    dispatch({ type: 'incident', incident });
    publishIncident(incident);
    if (state.candidate?.mode !== 'public-demo') {
      void api.saveIncident(incident).catch(() => undefined);
    }
  }, [state.candidate?.mode]);
  const recordObservationCondition = useCallback((condition: ObservationCondition) => {
    dispatch({ type: 'observation', condition });
  }, []);
  const login = useCallback((candidate: Candidate) => dispatch({ type: 'login', candidate }), []);
  const startExam = useCallback(() => dispatch({ type: 'start' }), []);
  const finishExam = useCallback(() => {
    const now = Date.now();
    dispatch({ type: 'finish', finishedAt: new Date(now).toISOString(), reportExpiresAt: createReportExpiry(now) });
  }, []);
  const setAnswer = useCallback((answer: Answer) => dispatch({ type: 'answer', answer }), []);
  const setActiveQuestion = useCallback((questionId: string) => dispatch({ type: 'active', questionId }), []);
  const updateMetrics = useCallback((metrics: Partial<ProctorMetrics>) => dispatch({ type: 'metrics', metrics }), []);
  const completeWorkflowStep = useCallback((key: keyof ExamState['workflow']) => dispatch({ type: 'workflow', key }), []);
  const completeCalibration = useCallback((checks: string[], cameraCategory?: 'front' | 'rear' | 'unknown') => dispatch({ type: 'calibration', checks, cameraCategory }), []);
  const logout = useCallback(() => {
    localStorage.removeItem('mockExamToken');
    sessionStorage.removeItem('examSphereSession');
    sessionStorage.removeItem('examPhoneRoomId');
    dispatch({ type: 'logout' });
  }, []);

  const value = useMemo<ExamContextValue>(
    () => ({
      state,
      login,
      startExam,
      finishExam,
      setAnswer,
      setActiveQuestion,
      addIncident,
      recordObservationCondition,
      updateMetrics,
      completeWorkflowStep,
      completeCalibration,
      logout
    }),
    [addIncident, completeCalibration, completeWorkflowStep, finishExam, login, logout, recordObservationCondition, setActiveQuestion, setAnswer, startExam, state, updateMetrics]
  );

  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
}

export function useExam() {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExam must be used within ExamProvider');
  }
  return context;
}
